import React from "react";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../../layout/pageLayout";
import { DesktopKpiStrip } from "../../shared/DesktopKpiStrip";
import { DeskEmpty, DeskPanel, DeskPill, DeskTable } from "../../desktop/DeskTable";
import { formatEstimateStatus } from "../../../helpers/estimates";
import { formatCurrency } from "../../../helpers/estimateTotals";
import { estimateStatusTone } from "./estimateDesktopUi";
import "./estimateDesktop.css";

/**
 * Desktop Estimate workspace (the editing home): locations as a structured
 * table, estimate identity / header form in a side panel. Presentational — all
 * state and handlers stay in EstimateWorkspaceScreen.
 *
 * rows: [{ id, name, featureCount, sqft, total, jobWide }]
 */
export function EstimateWorkspaceDesktop({
  title,
  clientName,
  estimateNumber,
  status,
  total,
  rows,
  editable,
  builderError,
  onBack,
  onOpenLocation,
  onAddLocation,
  onOpenPricing,
  headerForm,
  children,
}) {
  const columns = [
    {
      key: "location",
      label: "Location",
      width: "44%",
      primary: true,
      render: (row) => (
        <span className="dt-item__text">
          <span className="dt-strong">{row.name}</span>
          {row.jobWide ? <span className="dt-sub">Job-wide scope</span> : null}
        </span>
      ),
    },
    {
      key: "features",
      label: "Features",
      align: "right",
      width: "18%",
      render: (row) => <span className="dt-num">{row.featureCount}</span>,
    },
    {
      key: "area",
      label: "Area",
      align: "right",
      width: "18%",
      render: (row) =>
        row.sqft > 0 ? <span className="dt-num">{row.sqft.toFixed(0)} ft²</span> : <span className="dt-muted">—</span>,
    },
    {
      key: "total",
      label: "Total",
      align: "right",
      width: "20%",
      render: (row) =>
        row.total > 0 ? <span className="dt-num">{formatCurrency(row.total)}</span> : <span className="dt-muted">—</span>,
    },
  ];

  const physicalCount = rows.filter((row) => !row.jobWide).length;

  return (
    <CrewPilotModuleShell
      title={title}
      subtitle={clientName}
      onBack={onBack}
      desktopBackLabel="Estimate Summary"
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="cdh-actions">
          {editable ? (
            <button type="button" className="dt-btn" onClick={onAddLocation}>
              + Add Location
            </button>
          ) : null}
          <button type="button" className="dt-btn dt-btn--primary" onClick={onOpenPricing}>
            Pricing &amp; Review
          </button>
        </div>
      }
    >
      {builderError ? <div className="est-inline-error">{builderError}</div> : null}

      <DesktopKpiStrip
        ariaLabel="Estimate overview"
        className="est-d-kpi3"
        tiles={[
          { key: "total", value: formatCurrency(total), label: "Estimate Total", tone: "gold" },
          { key: "locs", value: String(physicalCount), label: physicalCount === 1 ? "Location" : "Locations", tone: "blue" },
          {
            key: "feats",
            value: String(rows.reduce((sum, row) => sum + row.featureCount, 0)),
            label: "Features",
            tone: "muted",
          },
        ]}
      />

      <div className="dp-grid dp-grid--main-side">
        <DeskPanel title="Locations" className="dp--flush">
          {rows.length === 0 ? (
            <DeskEmpty title="No locations yet">
              {editable ? (
                <span className="est-d-empty-cta">
                  <span>Add your first location to start building this estimate.</span>
                  <button type="button" className="dt-btn dt-btn--primary" onClick={onAddLocation}>
                    + Add First Location
                  </button>
                </span>
              ) : (
                "This estimate has no locations."
              )}
            </DeskEmpty>
          ) : (
            <DeskTable
              testId="estimate-locations-table"
              className="est-d-flat"
              caption="Estimate locations"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              onOpen={(row) => onOpenLocation(row.id)}
              openLabel={(row) => `Open ${row.name}`}
            />
          )}
        </DeskPanel>

        <aside className="est-d-side" aria-label="Estimate details">
          <DeskPanel title="Estimate">
            <div className="est-d-id">
              <span>{estimateNumber}</span>
              <DeskPill tone={estimateStatusTone(status)}>{formatEstimateStatus(status)}</DeskPill>
            </div>
            {headerForm}
          </DeskPanel>
        </aside>
      </div>
      {children}
    </CrewPilotModuleShell>
  );
}
